import { FC, useEffect, useState } from 'react';
import { LineItem } from '../buildInvoicesForm.helper';
import { Box, RailzInventoryItem, RailzTaxRate, RailzTrackingCategory, UtilButton } from 'vopay';
import InvoiceTableRow from './InvoiceTableRow';
import styles from './InvoicesTable.module.scss';

type ExtendedLineItem = LineItem & { isValid: boolean; hasBeenTouched: boolean };

interface InvoicesTableProps {
  onChange: (taxItems: LineItem[], isValid: boolean) => void;
  afterHasTouched: boolean;
  inventory: RailzInventoryItem[];
  salesTaxes: RailzTaxRate[];
  trackingCategories: RailzTrackingCategory[];
  taxCalculation: 'inclusive' | 'exclusive' | 'outOfScope';
}

const InvoicesTable: FC<InvoicesTableProps> = ({
  onChange,
  inventory,
  salesTaxes,
  trackingCategories,
  afterHasTouched,
  taxCalculation,
}) => {
  const emptyRecord = {
    Description: '',
    Quantity: '',
    Rate: '',
    Amount: '',
    InventoryRefID: '',
    TaxRateID: '',
    TrackingCategoryID: '',
    isValid: false,
    hasBeenTouched: afterHasTouched,
  };
  const [lineItems, setLineItems] = useState<ExtendedLineItem[]>([emptyRecord]);
  const addRow = () => {
    const updatedLineItems = [...lineItems, emptyRecord];
    setLineItems(updatedLineItems);
    onChange(
      updatedLineItems,
      updatedLineItems.every((item) => item.isValid),
    );
  };
  const removeRow = (index: number) => {
    const updatedLineItems = lineItems.filter((_, i) => i !== index);
    setLineItems(updatedLineItems);
    onChange(
      updatedLineItems,
      updatedLineItems.every((item) => item.isValid),
    );
  };
  const updateRow = (index: number, lineItem: LineItem, isValid: boolean) => {
    const updatedLineItems = lineItems.map((item, i) => {
      if (i === index) {
        return {
          ...lineItem,
          isValid,
          hasBeenTouched: true,
        };
      }
      return item;
    });
    setLineItems(updatedLineItems);
    onChange(
      updatedLineItems,
      updatedLineItems.every((item) => item.isValid),
    );
  };

  useEffect(() => {
    if (afterHasTouched) {
      const updatedLineItems = lineItems.map((item) => {
        return {
          ...item,
          hasBeenTouched: true,
        };
      });
      setLineItems(updatedLineItems);
      onChange(
        updatedLineItems,
        updatedLineItems.every((item) => item.isValid),
      );
    }
  }, [afterHasTouched]);

  return (
    <Box gapSize="12px" flx="col">
      <table className={styles.table}>
        <thead>
          <tr>
            <th>#</th>
            <th>Product/Service</th>
            <th>Description</th>
            <th>Qty</th>
            <th>Rate</th>
            <th>Total</th>
            {taxCalculation !== 'outOfScope' && <th>Sales Tax</th>}
            <th>Class</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, index) => {
            const nextRowUpdated = lineItems.slice(index + 1).some((nextItem) => nextItem.hasBeenTouched);
            return (
              <InvoiceTableRow
                key={index.toString()}
                index={index}
                inventory={inventory}
                salesTaxes={salesTaxes}
                trackingCategories={trackingCategories}
                values={item}
                nextRowUpdated={nextRowUpdated}
                taxCalculation={taxCalculation}
                onChange={updateRow}
                onDelete={removeRow}
              />
            );
          })}
        </tbody>
      </table>
      <UtilButton
        iconLeftProps={{
          icon: 'add',
        }}
        size="medium"
        onClick={addRow}
      >
        Add More
      </UtilButton>
    </Box>
  );
};

export default InvoicesTable;
